import { alpha, type Theme } from "@mui/material/styles";

import type { RowChangeTone } from "~/utils/getRowChangeDisplay";

export type PanelSide = "buy" | "sell";

const INK = "#0B0C0E";
const WHITE = "#fff";
const PANEL = "#FAFAFA";
const BORDER = "#E4E4E7";
const BORDER_STRONG = "#D4D4D8";
const DIVIDER = "#EDEDEF";
const HOVER = "#F4F4F5";
const MUTED = "#71717A";
const VALUE = "#52525B";
const WORDING = "#3F3F46";
const WITHHELD = "#A1A1AA";
const GROWTH = "#1E9E59";
const GROWTH_TINT = "#E9F8F0";
const DECLINE = "#C62828";
const DECLINE_TINT = "#FDECEC";
const DARK_TINT = 0.12;

export const TRADE_TEXT = { ink: INK, white: WHITE };

const isDark = (theme: Theme) => theme.palette.mode === "dark";

export const inkColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.text.primary : INK;

export const edgeColor = (theme: Theme) => (isDark(theme) ? WHITE : INK);

export const mutedColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.text.secondary : MUTED;

export const valueColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.text.secondary : VALUE;

export const wordingColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.text.primary : WORDING;

export const withheldColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.text.disabled : WITHHELD;

export const dividerColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.divider : DIVIDER;

export const borderColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.divider : BORDER;

export const strongBorderColor = (theme: Theme) =>
  isDark(theme) ? alpha(theme.palette.common.white, 0.23) : BORDER_STRONG;

export const paperColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.background.paper : WHITE;

export const panelColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.background.default : PANEL;

export const hoverColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.action.hover : HOVER;

export const warningColor = (theme: Theme) => theme.palette.warning.main;

export const urgentColor = (theme: Theme) =>
  isDark(theme) ? theme.palette.error.main : DECLINE;

export const sideColor = (theme: Theme, side: PanelSide) => {
  if (side === "buy") {
    return isDark(theme) ? theme.palette.success.main : GROWTH;
  }

  return urgentColor(theme);
};

export const toneColor = (theme: Theme, tone: RowChangeTone) =>
  tone === "none"
    ? mutedColor(theme)
    : sideColor(theme, tone === "up" ? "buy" : "sell");

export const sideTint = (theme: Theme, side: PanelSide) => {
  if (isDark(theme)) {
    return alpha(sideColor(theme, side), DARK_TINT);
  }

  return side === "buy" ? GROWTH_TINT : DECLINE_TINT;
};
